import React, { useRef, useEffect, useState } from 'react';
import { Box, Heading, VStack, Button, HStack, Text } from '@bpba/ui-components';
import { FaLocationDot, FaDownload, FaCircleExclamation } from "react-icons/fa6";
import { BsFillTelephoneFill } from "react-icons/bs";
import { MdOutlinePhoneIphone, MdOutlineAlternateEmail, MdRealEstateAgent } from "react-icons/md";
import { useNavigate, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import { ModuleContainerGeneric } from '@bpba/modules-components';
import { Nav } from '../components/shared/Nav';
import { MenuIconComponent } from '../components/novedades/MenuIconComponent';
import { MenuIcon } from '../model/novedades/MenuIcon';
import { MapaOSM } from '../components/MapaOSM';
import {
    normalizarTexto,
    corregirTildesTexto,
    extraerLatLngDeGoogleMaps,
    formatearMonedaARS,
    formatearFecha
} from '../utils/ajusteDatosBackend';
import { FullScreenGallery } from '../components/FullScreenGallery';
import { getInmuebleById, getLocalidades, buildAssetUrl } from '../utils/inmuebles.service';
import { ScrollToTop } from '../components/shared/ScrollToTop';
import { LoadingComponent } from '../components/shared/LoadingComponent';
import { NotFound } from '../components/error/NotFound';

export const Detalle: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    // El estado inicial ahora es estrictamente null hasta que responda la API
    const [inmueble, setInmueble] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const swiperRef = useRef<SwiperRef>(null);
    type VistaActiva = 'galeria' | 'mapa';
    const [vista, setVista] = React.useState<VistaActiva>('galeria');

    const [fullscreen, setFullscreen] = useState(false);
    const [slideInicial, setSlideInicial] = useState(0);
    const [localidadesMap, setLocalidadesMap] = useState<Record<number, string>>({});

    // 1. Petición del inmueble específico por ID
    useEffect(() => {
        if (!id) return;

        setLoading(true);
        setError(null);

        getInmuebleById(id)
            .then((data) => {
                // Si el array viene como [ {} ] se toma el primer elemento. Si no, el objeto directo.
                const resultado = Array.isArray(data) ? data[0] : data;

                if (!resultado) {
                    throw new Error('Inmueble vacío en la respuesta');
                }

                setInmueble(resultado);
            })
            .catch((err) => {
                console.error('Error al cargar inmueble:', err);
                setError('No se pudo cargar la información del inmueble.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id]);

    // 2. Carga de localidades para el diccionario (Protegido contra 404)
    useEffect(() => {
        getLocalidades()
            .then((data) => {
                if (data) setLocalidadesMap(data);
            })
            .catch((err) => {
                // Catch 404
                console.warn('No se pudieron cargar las localidades de la API (404/Error). Se usarán los IDs numéricos.', err);
                setLocalidadesMap({});
            });
    }, []);

    // 3. Efecto para el Autoplay de Swiper
    useEffect(() => {
        if (swiperRef?.current?.swiper?.autoplay) {
            swiperRef.current.swiper.autoplay.running = true;
        }
    }, [inmueble, vista]); // Se reactiva si cambia el inmueble o la vista

    // 4. Manejo del Fullscreen (Escape Lock)
    useEffect(() => {
        if (!fullscreen) return;

        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handler);

        return () => {
            window.removeEventListener('keydown', handler);
            document.body.style.overflow = 'auto';
        };
    }, [fullscreen]);

    // Render de estados de carga y error
    if (loading) {
        return <LoadingComponent />;
    }

    if (error || !inmueble) {
        return (
            <ModuleContainerGeneric padding="20px">
                <NotFound />
            </ModuleContainerGeneric>
        );
    }

    // 1. Setea el array original de imágenes (o un array vacío si no hay)
    const imagenesOriginales = inmueble.imagenes ?? [];

    // 2. Ordenar de forma numérica según el último número de la clave
    const imagenesOrdenadas = [...imagenesOriginales].sort((a: any, b: any) => {
        // Expresión regular que busca los dígitos (\d+) al final ($) del string
        const regex = /(\d+)$/;

        const numA = parseInt(a.clave.match(regex)?.[0] || '0', 10);
        const numB = parseInt(b.clave.match(regex)?.[0] || '0', 10);

        return numA - numB; // Orden ascendente
    });

    // 3. Mapeo de imágenes ya ordenadas con la URL del CDN
    const imagenes = imagenesOrdenadas.map((img: any) => buildAssetUrl(img.clave));

    const coords = extraerLatLngDeGoogleMaps(inmueble.linkGoogleMaps);

    const mostrarSwiper = () => setVista('galeria');
    const mostrarMapa = () => setVista('mapa');

    function getNombreLocalidad(codigo: number | string) {
        return localidadesMap[Number(codigo)] ?? codigo.toString();
    }

    return (
        <>
            <ScrollToTop />
            <ModuleContainerGeneric width="100%" height={'auto'} flexDirection={'column'} padding={'20px'}>
                <Heading lineHeight={'normal'} marginTop={'0px !important'}>Detalle del inmueble</Heading>

                <Box display={'flex'} flexDirection={['column', null, 'row']} height={['auto', 'auto', '500px', '500px']} marginBottom={'16px'}>
                    <Box width={['100%', '100%', '50%', '50%']} overflow={'hidden'} position={'relative'} zIndex={0} style={{ isolation: 'isolate' }}>
                        <Nav display="flex" position="absolute" top={0} zIndex={10} justifyContent="center" flexDirection="column" height={180}>
                            <MenuIconComponent
                                item={new MenuIcon(0, "Imágenes", undefined, undefined, "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 640'><path d='M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 160C544 124.7 515.3 96 480 96L160 96zM224 176C250.5 176 272 197.5 272 224C272 250.5 250.5 272 224 272C197.5 272 176 250.5 176 224C176 197.5 197.5 176 224 176zM368 288C376.4 288 384.1 292.4 388.5 299.5L476.5 443.5C481 450.9 481.2 460.2 477 467.8C472.8 475.4 464.7 480 456 480L184 480C175.1 480 166.8 475 162.7 467.1C158.6 459.2 159.2 449.6 164.3 442.3L220.3 362.3C224.8 355.9 232.1 352.1 240 352.1C247.9 352.1 255.2 355.9 259.7 362.3L286.1 400.1L347.5 299.6C351.9 292.5 359.6 288.1 368 288.1z'/></svg>")}
                                funcion={mostrarSwiper}
                            />
                            <MenuIconComponent
                                item={new MenuIcon(1, "Ubicación", undefined, undefined, "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 640'><path d='M576 112C576 100.9 570.3 90.6 560.8 84.8C551.3 79 539.6 78.4 529.7 83.4L413.5 141.5L234.1 81.6C226 78.9 217.3 79.5 209.7 83.3L81.7 147.3C70.8 152.8 64 163.9 64 176L64 528C64 539.1 69.7 549.4 79.2 555.2C88.7 561 100.4 561.6 110.3 556.6L226.4 498.5L399.7 556.3C395.4 549.9 391.2 543.2 387.1 536.4C376.1 518.1 365.2 497.1 357.1 474.6L255.9 440.9L255.9 156.4L383.9 199.1L383.9 298.4C414.9 262.6 460.9 240 511.9 240C534.5 240 556.1 244.4 575.9 252.5L576 112zM392 405.9C392 474.8 456.1 556.3 490.6 595.2C502.2 608.2 521.9 608.2 533.5 595.2C568 556.3 632.1 474.8 632.1 405.9C632.1 340.8 578.4 288 512.1 288C445.8 288 392 340.8 392 405.9z'/></svg>")}
                                funcion={mostrarMapa}
                            />
                        </Nav>
                        {vista === 'galeria' ? (
                            <Swiper
                                modules={[Autoplay, Pagination]}
                                autoplay={{ delay: 5000, disableOnInteraction: true }}
                                spaceBetween={16}
                                ref={swiperRef}
                                pagination={{ clickable: true }}
                                style={{ width: '100%', height: '500px', borderRadius: '8px 32px 8px 32px', margin: 'unset' }}
                            >
                                {imagenes.length > 0 ? (
                                    imagenes.map((img: string, index: number) => (
                                        <SwiperSlide
                                            key={index}
                                            style={{
                                                height: '500px',
                                                backgroundImage: `url(${img})`,
                                                backgroundSize: 'cover',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                setSlideInicial(index);
                                                setFullscreen(true);
                                            }}
                                        />
                                    ))
                                ) : (
                                    <Box height="500px" display="flex" alignItems="center" justifyContent="center" backgroundColor="#f2f2f2" borderRadius="8px 32px 8px 32px">
                                        <Text fontSize="16px" color="gray">Aún no hay imágenes para este inmueble</Text>
                                    </Box>
                                )}
                            </Swiper>
                        ) : (
                            <Box width={'100%'} height={'100%'} borderRadius={'8px 32px 8px 32px'} position={'relative'} zIndex={1}>
                                {coords ? (
                                    <MapaOSM
                                        lat={coords.lat}
                                        lng={coords.lng}
                                        popupText={`${corregirTildesTexto(normalizarTexto(inmueble.direccion + ' ' + inmueble.numero))} - ${getNombreLocalidad(inmueble.localidad)}`}
                                        height={500}
                                        zoom={16}
                                    />
                                ) : (
                                    <Box width="100%" height="500px" borderRadius="8px 32px 8px 32px" backgroundColor="#f2f2f2" display="flex" alignItems="center" justifyContent="center">
                                        <Text>No se pudo obtener la ubicación del inmueble.</Text>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                    {fullscreen && (
                        <FullScreenGallery imagenes={imagenes} slideInicial={slideInicial} onClose={() => setFullscreen(false)} />
                    )}

                    <VStack width={['100%', '100%', '50%', '50%']} height={'fit-content'} marginLeft={['0px', null, '32px']} marginTop={['16px', null, '0px']} borderRadius={'8px 32px 8px 32px'}>
                        <Text fontSize={'22px'} lineHeight={'38px'} textAlign={'left'} fontWeight={'500'} color={'rgb(39, 157, 46)'} marginBottom={'5px !important'}>
                            {corregirTildesTexto(normalizarTexto(inmueble.direccion))} N° {inmueble.numero} {inmueble.piso ? `Piso ${inmueble.piso}` : ''} {inmueble.depto ? `Depto ${inmueble.depto}` : ''}
                        </Text>
                        <Text>{inmueble.caracteristicas}</Text>

                        <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'10px'}>
                            <FaLocationDot style={{ color: '#279d2e', width: '18px', height: '30px', marginRight: '8px', marginBottom: '5px' }} />
                            <Text marginLeft={'2px !important'}>{normalizarTexto(getNombreLocalidad(inmueble.localidad))}</Text>
                        </HStack>

                        {inmueble.estadoVenta && (
                            <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'10px'}>
                                <MdRealEstateAgent style={{ color: '#279d2e', width: '20px', height: '30px', marginRight: '8px', marginBottom: '5px' }} />
                                <Text style={{ width: '90%' }}>{normalizarTexto(inmueble.estadoVenta)}</Text>
                            </HStack>
                        )}

                        {inmueble.pliego?.[0]?.clave && (
                            <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'10px'}>
                                <FaDownload style={{ color: '#279d2e', width: '20px', height: '30px', marginRight: '8px' }} />

                                <a
                                    href={buildAssetUrl(inmueble.pliego[0].clave)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'underline', fontWeight: 700, color: '#3c3c3b' }}
                                >
                                    Descargar pliego
                                </a>
                            </HStack>
                        )}

                        <Box marginTop={'40px'} width={'100%'} height={'90px'} display={'flex'} flexDirection={'column'} justifyContent={'space-between'} alignItems={'center'}>
                            <Button
                                variant="secondary"
                                width={'100%'}
                                onClick={() => navigate('/ConsultaUsuarioInmueble', { state: { idInmueble: inmueble.inmueble } })}>
                                Contactar al Banco
                            </Button>
                            <Button type={'button'} variant="secondary" width={'100%'} style={{ backgroundColor: "transparent", borderRadius: "3px 8px 3px 8px", border: "solid 1px", borderColor: "#3c3c3b", color: "#3c3c3b", height: "calc(1.8em + 0.75rem + 2px)" }} onClick={() => navigate('/Index')}>Volver al Buscador</Button>
                        </Box>
                    </VStack>
                </Box>

                <HStack marginTop={'32px'}>
                    <Text fontSize={'22px'} fontWeight={'400'} lineHeight={'27px'} color={'#3c3c3b'} marginBottom={'10px !important'}>Características del inmueble</Text>
                </HStack>

                <HStack>
                    <VStack>
                        {inmueble.unidadFuncional && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text>Unidad funcional: {inmueble.unidadFuncional}</Text>
                            </HStack>
                        )}

                        {inmueble.unidadComplementaria && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text>Unidad complementaria: {inmueble.unidadComplementaria}</Text>
                            </HStack>
                        )}

                        {Number(inmueble.superficieTerreno) > 0 && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Superficie del terreno: {inmueble.superficieTerreno} m<sup>2</sup></Text>
                            </HStack>
                        )}

                        {Number(inmueble.superficieCubierta) > 0 && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Superficie cubierta: {inmueble.superficieCubierta} m<sup>2</sup></Text>
                            </HStack>
                        )}

                        {Number(inmueble.superficieSemicubierta) > 0 && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Superficie semicubierta: {inmueble.superficieSemicubierta} m<sup>2</sup></Text>
                            </HStack>
                        )}

                        {Number(inmueble.superficieDescubierta) > 0 && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Superficie descubierta: {inmueble.superficieDescubierta} m<sup>2</sup></Text>
                            </HStack>
                        )}

                        {Number(inmueble.superficieTotal) > 0 && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Superficie total: {inmueble.superficieTotal} m<sup>2</sup></Text>
                            </HStack>
                        )}
                    </VStack>
                    <VStack>
                        {inmueble.ultimoValorReferencia && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Último valor de referencia: {formatearMonedaARS(inmueble.ultimoValorReferencia)}</Text>
                            </HStack>
                        )}

                        {inmueble.fechaUltimoValorRef && (
                            <HStack alignItems={'center'} marginBottom={'7px'}>
                                <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                                <Text fontSize={'15px'}>Fecha del último valor de referencia: {formatearFecha(inmueble.fechaUltimoValorRef.split(' ')[0])}</Text>
                            </HStack>
                        )}

                        {inmueble.fechaLicitacionPublica !== "" && inmueble.fechaLicitacionPublica !== null ? (
                        <HStack alignItems={'center'} marginBottom={'7px'}>
                            <img src='/CDN/Get/Bullet_listas' width={'18px'} height={'23px'} style={{ marginRight: '10px' }} />
                            <Text fontSize={'15px'}>Fecha de licitación pública: {formatearFecha(inmueble.fechaLicitacionPublica)}</Text>
                        </HStack>
                    ) : null}
                    </VStack>
                </HStack>

                <VStack borderRadius={'8px 32px 8px 32px'} backgroundColor={'#dedee3'} padding={'20px'} marginTop={'32px'}>
                    <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginBottom={'10px'}>
                        <BsFillTelephoneFill style={{ color: '#279d2e', width: '30px', height: '30px', marginRight: '10px' }} />
                        <Text fontWeight={'600'} fontSize={'18px'} color={'#279d2e'}>Contacto</Text>
                    </HStack>
                    <Text fontSize={'17px'}>Depto. de Administración de Inmuebles. Guanahani 580, 3° Nivel, Núcleo 'A' CABA.</Text>
                    <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'10px'}>
                        <MdOutlinePhoneIphone style={{ width: '20px', height: '30px', marginRight: '3px' }} />
                        <Text marginLeft={'2px !important'} fontSize={'17px'}><a style={{ textDecoration: 'underline', fontWeight: '700', color: '#3c3c3b' }} href='tel:41262898'>4126-2898</a> o <a href='tel:41262734' style={{ textDecoration: 'underline', fontWeight: '700', color: '#3c3c3b' }}>4126-2734</a></Text>
                    </HStack>
                    <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'10px'}>
                        <MdOutlineAlternateEmail style={{ width: '20px', height: '30px', marginRight: '3px' }} />
                        <Text marginLeft={'2px !important'} fontSize={'17px'}><a href='mailto:ventadeinmuebles@bpba.com.ar' style={{ textDecoration: 'underline', fontWeight: '700', color: '#3c3c3b' }}>ventadeinmuebles@bpba.com.ar</a></Text>
                    </HStack>

                    <HStack display={'flex'} flexWrap={'wrap'} alignItems={'center'} marginTop={'30px'} marginBottom={'10px'}>
                        <FaCircleExclamation style={{ color: '#279d2e', width: '30px', height: '30px', marginRight: '10px' }} />
                        <Text fontWeight={'600'} fontSize={'18px'} color={'#279d2e'}>Importante</Text>
                    </HStack>
                    <Text fontSize={'17px'}>De acuerdo a normas vigentes, será requisito indispensable para todos aquellos oferentes interesados, la presentación de Certificación de Origen de Fondos firmada por Contador Público y certificada por el Consejo Profesional de Ciencias Económicas en caso de personas físicas; o en caso de personas jurídicas, último Estado Contable, firmada por Contador Público y certificada por el Consejo Profesional de Ciencias Económicas.</Text>
                </VStack>
            </ModuleContainerGeneric>
        </>
    );
};